import { usesHostedBilling, usesStoreBilling, isMacAppStore } from '@/lib/appDistribution';
import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform, Alert } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { getCreditPackIapProductId } from '@/hooks/useIosIap';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PayWithBitcoinRow from '@/components/Credits/PayWithBitcoinRow';
import { useModalStore, useAuthStore } from '@/store';
import { trackEvent } from '@/constants/utils/analytics';

const createStyles = (colors, theme, isSM) => StyleSheet.create({
  creditPackWrapper: {
    width: '100%',
  },
  creditPack: {
    backgroundColor: colors.solid_color_block,
    borderRadius: 12,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: isSM ? 50 : 56,
    paddingVertical: 8,
    cursor: 'pointer',
  },
  creditPackHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  creditPackLeft: {
    flex: 1,
    paddingRight: 8,
    minWidth: 0, // allow flex children to shrink below content size
  },
  creditPackHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isSM ? 6 : 8,
    flexWrap: 'wrap',
  },
  creditIconContainer: {
    width: isSM ? 32 : 36,
    height: isSM ? 32 : 36,
    borderRadius: 8,
    backgroundColor: colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  creditPackTitle: {
    fontSize: isSM ? 16 : 18,
    fontWeight: '700',
    lineHeight: isSM ? 20 : 22,
    flexShrink: 1,
  },
  popularBadge: {
    backgroundColor: 'rgba(0, 67, 255, 0.1)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: 'rgb(115, 126, 255)',
    flexShrink: 0,
  },
  popularBadgeText: {
    textTransform: 'uppercase',
    letterSpacing: isSM ? 0 : 0.3,
    color: 'rgb(115, 126, 255)',
    fontSize: isSM ? 10 : 11,
  },
  bestValueBadge: {
    backgroundColor: 'rgba(0, 255, 0, 0.13)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: 'rgb(35, 200, 45)',
    flexShrink: 0,
  },
  bestValueBadgeText: {
    textTransform: 'uppercase',
    letterSpacing: isSM ? 0 : 0.3,
    color: 'rgb(35, 200, 45)',
    fontSize: isSM ? 10 : 11,
  },
  creditPackRight: {
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  priceButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 3,
    backgroundColor: colors.accent_color_primary,
    borderRadius: 8,
    height: isSM ? 32 : 36,
    minWidth: isSM ? 135 : 148,
    overflow: 'hidden',
  },
  priceButtonContainerHover: {
    opacity: 0.85,
  },
  priceSection: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  creditPackPrice: {
    fontSize: isSM ? 14 : 16,
    fontWeight: '700',
    lineHeight: isSM ? 18 : 20,
    color: colors.accent_text_color,
  },
  buyButtonSection: {
    marginLeft: 'auto',
    height: isSM ? 26 : 30,
    minWidth: isSM ? 56 : 70,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: colors.accent_text_color + '20',
  },
  buyButtonIcon: {
    marginTop: 1,
  },
  buyButtonText: {
    color: colors.accent_text_color,
  },
});

function CreditPackListItem({ 
  id: creditPackId,
  credits,
  price,
  name,
  isPopular = false,
  isBestValue = false,
  promoCode,
}) {
  const { colors, theme } = useTheme();
  const { isSM } = useResponsiveness();
  const { openCreditPacksCheckout, usesWhopCheckout, iap } = useGabPurchase();
  const { openModal } = useModalStore(state => state.actions);
  const [loadingCreditPack, setLoadingCreditPack] = useState(null);
  const styles = useMemo(() => createStyles(colors, theme, isSM), [colors, theme, isSM]);
  
  // Check if user has stored payment method for quick purchase
  const hasPaymentMethod = useAuthStore(state => state.user?.valmar?.hasPaymentMethod);
  const maskedCardData = useAuthStore(state => state.user?.valmar?.maskedCardData);
  
  // Whop checkout (international or existing Whop Plus) cannot use Valmar quick purchase
  const canQuickPurchase = usesHostedBilling() && !usesWhopCheckout && hasPaymentMethod && maskedCardData?.maskedNumber;

  // Handle credit pack purchase - use quick purchase if saved card (US), else IAP (iOS) or web checkout
  const handlePurchaseCreditPack = useCallback(async (packId, packCredits) => {
    trackEvent('conversion', 'credit_pack_selected', String(packCredits), price);
    setLoadingCreditPack(packId);
    try {
      if (canQuickPurchase) {
        openModal('QuickPurchaseConfirmModal', { creditPackId: packId });
      } else {
        await openCreditPacksCheckout({ creditPackId: packId, credits: packCredits, promoCode });
      }
    } catch (error) {
      console.error('Credit pack purchase error:', error);
      if (Platform.OS !== 'web') {
        Alert.alert('Purchase Error', error?.message || 'Failed to purchase credit pack. Please try again.');
      }
    } finally {
      setLoadingCreditPack(null);
    }
  }, [canQuickPurchase, openModal, openCreditPacksCheckout, promoCode, price]);

  // Use localized StoreKit price on iOS/Android when available
  const iapProductId = usesStoreBilling() ? getCreditPackIapProductId(credits) : null;
  const iapProduct = iapProductId ? iap?.getProduct?.(iapProductId) : null;
  const priceLabel = (isMacAppStore() ? (iapProduct?.localizedPrice || 'Unavailable') : null) || iapProduct?.localizedPrice
    || (iapProduct?.price != null ? `$${parseFloat(iapProduct.price).toFixed(2)}` : null)
    || `$${(price ?? 0).toFixed(2)}`;

  if (isMacAppStore() && !iapProductId) return null;
  return (
    <View style={styles.creditPackWrapper}>
      <Pressable
        style={({ hovered }) => [
          styles.creditPack,
          hovered && styles.creditPackHovered,
        ]}
        onPress={() => handlePurchaseCreditPack(creditPackId, credits)}
        disabled={loadingCreditPack !== null}
      >
        <View style={styles.creditPackLeft}>
          <View style={styles.creditPackHeader}>
            <View style={styles.creditIconContainer}>
              <Icon name="bolt" size={isSM ? 14 : 16} color={colors.text_color_primary} solid />
            </View>
            <Text style={styles.creditPackTitle} color="primary" numberOfLines={1}>
              {name || `${credits.toLocaleString()} Credits`}
            </Text>
            {isPopular && (
              <View style={styles.popularBadge}>
                <Text style={styles.popularBadgeText} weight="bold">
                  Popular
                </Text>
              </View>
            )}
            {isBestValue && (
              <View style={styles.bestValueBadge}>
                <Text style={styles.bestValueBadgeText} weight="bold">
                  {isSM ? 'Best Deal' : 'Best Value!'}
                </Text>
              </View>
            )}
          </View>
        </View>
        <View style={styles.creditPackRight}>
          <View style={styles.priceButtonContainer}>
            <View style={styles.priceSection}>
              <Text style={styles.creditPackPrice}>
                {priceLabel}
              </Text>
            </View>
            <View style={styles.buyButtonSection}>
              {Platform.OS === 'web' && loadingCreditPack !== creditPackId && (
                <Icon name="credit-card" size={isSM ? 12 : 13} color={colors.accent_text_color} solid style={styles.buyButtonIcon} />
              )}
              <Text style={styles.buyButtonText} weight='bold'>
                {loadingCreditPack === creditPackId ? 'Buying...' : (Platform.OS === 'web' ? 'Card' : 'Buy')}
              </Text>
            </View>
          </View>
        </View>
      </Pressable>

      <PayWithBitcoinRow
        creditPackId={creditPackId}
        price={price}
        disabled={loadingCreditPack !== null}
      />
    </View>
  );
} 

export default React.memo(CreditPackListItem);